import { useCallback, useEffect, useState } from "react"
import { AddWorkflowFormProps } from "./AddScopeWorkflowForm.types"
import WorkflowInfoForm from "./ScopeWorkflowForm/WorkflowInfoForm"
import SectionForm from "./SectionForm/SectionForm"
import OverviewForm from "./OverviewForm/OverviewForm"
import ScopeWorkflowFormType from "../../../utils/ScopeWorkflowFormType"
import { CAMERA_OPTICAL_DIAMETER, MAX_PIPE_DIAMETER_FOR_DEFAULT_ANGLES, MAX_SCOPE_WORKFLOW_SECTIONS, MAX_SECTION_ANGLES } from "../../../utils/constants"
import useScopeWorkflowInputs from "../../../hooks/useAngularScopeWorkflowInputs"
import { showErrorToast } from "../../../utils/utils"
import { StarAPIFetchState } from "../../../types/types"
import { AngularScopeWorkflow } from "../../../types/starAPIResponseTypes"

import WorkflowType from "../../../enums/workflowType"
import { getWorkflow } from "../../../service/workflowService"
import { STARAPIError } from "../../../service/service"

const useScopeAddWorkflow = (props: AddWorkflowFormProps) => {
  const {
    claddingMaterials,
    insulationMaterials,
    locations,
    onSubmit,
    scopeWorkflowId,
    showForm,
    formType,
    onHide 
  } = props

  const [scopeWorkflow, setScopeWorkflow] = useState<StarAPIFetchState<AngularScopeWorkflow>>({ status: "idle" })
  const [currentSection, setCurrentSection] = useState<number>(0)
  const [currentForm, setCurrentForm] = useState<number>(0)
  const [showSectionAngleMenu, setShowSectionAngleMenu] = useState<boolean>(false)

  const [{
    filename,
    pipeId,
    location,
    pipeLength,
    pipeTemperature,
    pipeDiameter,
    assetType,
    workflowStart,
    workflowEnd,
    insulationMaterial,
    insulationThickness,
    claddingMaterial,
    claddingThickness,
    hasTracingCable,
    sectionStarts,
    sectionEnds,
    sectionAngles,
    sectionType,
    newAngle,
    selectedSectionTypes,
    onAddAngle,
    onRemoveAngle,
    onAddSectionValues,
    setScopeWorklfowForm,
    onRemoveCurrentSectionInputs,
    clearInputs,
    isScopeValidWorkflowInfo,
    isValidSectionForm
  }] = useScopeWorkflowInputs({
    locations: locations,
    insulationMaterials: insulationMaterials,
    claddingMaterials: claddingMaterials,
    currentSection: currentSection
  })

  useEffect(() => {
    const fetchScopeWorkflow = async () => {
      if (!scopeWorkflowId) { return }
      setScopeWorkflow({ status: "loading" })
      const response = await getWorkflow(scopeWorkflowId)
            
      if (response instanceof Error) {
        showErrorToast("Failed to load workflow scope.")
        setScopeWorkflow({ status: "error", error: response as STARAPIError })
        return
      }
      if (response.workflow_type !== WorkflowType.ANGULAR_SCOPE) {
        setScopeWorkflow({ status: "error", error: new Error("Invalid workflow type.") })
        return
      }
      setScopeWorkflow({ status: "success", data: response as AngularScopeWorkflow})
    }
    fetchScopeWorkflow()
  }, [scopeWorkflowId])

  useEffect(() => {
    clearInputs()
    
    switch (formType) {
      case ScopeWorkflowFormType.VIEW:
        setCurrentForm(2)
        break
      default:
        setCurrentForm(0)
    }
    
    if (scopeWorkflow.status !== "success") {
      return
    }
    const angularWorkflow = scopeWorkflow.data
    if (angularWorkflow === null) {
      return
    }
    
    switch (formType) {
      case ScopeWorkflowFormType.VIEW:
        setScopeWorklfowForm(angularWorkflow)
        setCurrentForm(2)
        return
      case ScopeWorkflowFormType.EDIT:
        if (scopeWorkflow === undefined) { return }
        setCurrentForm(0)
        setScopeWorklfowForm(angularWorkflow)
        return
    }
    setCurrentForm(0)
  }, [showForm, formType, scopeWorkflow, setScopeWorklfowForm, clearInputs])

  const onNextForm = async () => {    
    if (currentForm === 0) {
      if (!isScopeValidWorkflowInfo()) {
        return
      }
      if (sectionStarts.value.length === 0) {
        onAddSection()
      }      
    }

    if (currentForm === 1) {
      if (!isValidSectionForm()) {
        return
      }
    }

    if (currentForm === forms.length - 1 && formType === ScopeWorkflowFormType.VIEW) {
      setCurrentForm(0)
      onHide()
      return
    }

    if (currentForm === forms.length - 1) {
      onSubmit(getScopeWorkflow())
      setCurrentForm(0)
      onHide()
      return
    }

    setCurrentForm(currentForm + 1)
  }


  const onAddSection = () => {
    if (sectionStarts.value.length >= MAX_SCOPE_WORKFLOW_SECTIONS) { return }
    onAddSectionValues()
    setCurrentSection(sectionStarts.value.length)
  }

  const onRemoveCurrentSection = useCallback(() => {  
    if (sectionStarts.value.length === 1) { return }
    setCurrentSection((prev) => (prev === 0 ? 0 : prev-1))
    onRemoveCurrentSectionInputs()
  }, [sectionStarts, onRemoveCurrentSectionInputs])

  const getSections = () => {
    const sections = sectionStarts.value.map((start, i) => {
      return [parseInt(start.value), parseInt(sectionEnds.value[i].value), selectedSectionTypes[i]]
    })
    return JSON.stringify(sections)
  }

  const getAngles = () => {
    const angles = sectionAngles.map((angles, i) => {
      return angles.map(angle => parseInt(angle.value))
    })
    return JSON.stringify(angles)
  }

  const getScopeWorkflow = () => {
    let workflowId = undefined

    if (scopeWorkflow.status === "success") {
      workflowId = scopeWorkflow.data?.workflow_id
    }
    return {
      workflow_id: workflowId ?? 0,
      workflow_type: WorkflowType.ANGULAR_SCOPE.valueOf(),
      star_vision_version: null,
      client_upload_uuid: "",
      filename: filename.value,
      asset_id: pipeId.value,
      user_id: 0,
      created_at: `${Math.floor(Date.now() / 1000)}`,
      workflow_start: parseInt(workflowStart.value),
      workflow_end: parseInt(workflowEnd.value),
      cladding_material: claddingMaterial.values[claddingMaterial.selected],
      cladding_thickness: parseInt(claddingThickness.value),
      insulation_material: insulationMaterial.values[insulationMaterial.selected],
      insulation_thickness: parseInt(insulationThickness.value),
      asset_diameter: parseInt(pipeDiameter.value),
      asset_length: parseInt(pipeLength.value),
      asset_temp: parseInt(pipeTemperature.value),
      asset_type: assetType.selected,
      location: location.values[location.selected],
      has_tracing_cable: hasTracingCable.selected === 0 ? true : false,
      sections: getSections(),
      angles: getAngles(),
      is_fully_uploaded: false
    }
  }

  const onSelectSection = (section: number) => {
    if (formType === ScopeWorkflowFormType.VIEW) { return }
    setCurrentForm(section)
  }

  const onAddFourAngleScan = () => {
    if (sectionAngles[currentSection].length + 4 > MAX_SECTION_ANGLES) {
      showErrorToast("Adding 4 point star will exceed the max number of section angles.")
      return
    }
    onAddAngle({ value: "0", error: null})
    onAddAngle({ value: "90", error: null})
    onAddAngle({ value: "180", error: null})
    onAddAngle({ value: "270", error: null})
    setShowSectionAngleMenu(false)
  }

  const onAddEightAngleScan = () => {
    if (sectionAngles[currentSection].length + 8 > MAX_SECTION_ANGLES) {
      showErrorToast("Adding 8 point star will exceed the max number of section angles.")
      return
    }
    onAddAngle({ value: "0", error: null})
    onAddAngle({ value: "45", error: null})
    onAddAngle({ value: "90", error: null})
    onAddAngle({ value: "135", error: null})
    onAddAngle({ value: "180", error: null})
    onAddAngle({ value: "225", error: null})
    onAddAngle({ value: "270", error: null})
    onAddAngle({ value: "315", error: null})
    setShowSectionAngleMenu(false)
  }

  const addDefaultAngles = () => {
    if (isNaN(parseInt(pipeDiameter.value))) { return }

    let numberOfAngles = Math.ceil((Math.PI * parseInt(pipeDiameter.value)) / CAMERA_OPTICAL_DIAMETER)
    const currentNumberOfAngles = sectionAngles[currentSection].length

    const defaultAngles: number[] = []
    const increment = 360 / numberOfAngles    

    for (let i=0; i<numberOfAngles; i++) {      
      defaultAngles.push(Math.round(i*increment))
    }

    for (let i=0; i<defaultAngles.length; i++) {
      if (currentNumberOfAngles + i >= MAX_SECTION_ANGLES) {
        break
      }
      onAddAngle({ value: `${defaultAngles[i]}`, error: null})
    }
    setShowSectionAngleMenu(false)
  }

  const forms = [
    <WorkflowInfoForm
      filename={filename}
      pipeId={pipeId}
      location={location}
      pipeLength={pipeLength}
      pipeTemperature={pipeTemperature}
      pipeDiameter={pipeDiameter}
      assetType={assetType}
      workflowStart={workflowStart}
      workflowEnd={workflowEnd}
      insulationMaterial={insulationMaterial}
      insulationThickness={insulationThickness}
      claddingMaterial={claddingMaterial}
      claddingThickness={claddingThickness}
      hasTracingCable={hasTracingCable}
      onSubmit={onNextForm}
    />,
    <SectionForm 
      sectionTypes={sectionType}
      sectionStarts={sectionStarts.value}
      onChangeSectionStarts={sectionStarts.onChange}
      sectionEnds={sectionEnds.value}
      onChangeSectionEnds={sectionEnds.onChange}
      sectionAngles={sectionAngles}
      newAngle={newAngle}
      onAddAngle={onAddAngle}
      onRemoveAngle={onRemoveAngle}
      currentSection={currentSection}
      onAddSection={onAddSection}
      onRemoveCurrentSection={onRemoveCurrentSection}
      onSelectSection={(section: number) => setCurrentSection(section)}
      sectionAnglesEllipsisMenu={[
        { item: "Default angles", onClick: addDefaultAngles},
        { item: "4 point star", onClick: onAddFourAngleScan},
        { item: "8 point star", onClick: onAddEightAngleScan},
      ]}
      sectionAnglesEllipsisMenuState={{ value: showSectionAngleMenu, onChange: setShowSectionAngleMenu}}
    />,
    <OverviewForm workflow={getScopeWorkflow()} />
  ]

  return [{
    scopeWorkflow,
    currentForm: forms[currentForm],
    forms: ["WorkflowInfoForm", "SectionForm", "Overview"],
    currentFormIndex: currentForm,
    onSelectForm: onSelectSection,
    onNext: onNextForm,
  }]
}

export default useScopeAddWorkflow